/**
 * Email Event Type Metadata
 *
 * Display information and available variables for each email event type.
 * Used by UI components to show event details and validate templates.
 */

import type { EmailEventTypeMetadata } from "./schema/email-event.types";
import type { EmailEventType } from "../../../../../types/email-event.d";

/**
 * Metadata for all email event types
 */
export const EMAIL_EVENT_METADATA: Record<EmailEventType, EmailEventTypeMetadata> = {
  "ideia-comentario-mention": {
    event_type: "ideia-comentario-mention",
    display_name: "Menção em Comentário de Ideia",
    description: "Enviado quando um colaborador é mencionado em um comentário de ideia",
    category: "mentions",
    available_variables: [
      { name: "colaborador.nome", description: "Nome do colaborador mencionado", example: "João Silva" },
      { name: "colaborador.email", description: "Email do colaborador", example: "joao@empresa.com" },
      { name: "ideia.id", description: "ID da ideia", example: "idea123" },
      { name: "ideia.titulo", description: "Título da ideia", example: "Nova Proposta" },
      { name: "comentario.id", description: "ID do comentário", example: "cmt456" },
      { name: "comentario.comentario", description: "Texto do comentário", example: "Ótima ideia!" },
      { name: "url", description: "URL para acessar a ideia", example: "https://..." }
    ],
    subject_example: "Você foi mencionado | {{ideia.titulo}}",
    body_example:
      "<p>Olá {{colaborador.nome}},</p><p>Você foi mencionado em um comentário na ideia <b>{{ideia.titulo}}</b>:</p><blockquote>{{comentario.comentario}}</blockquote><p><a href='{{url}}'>Ver comentário</a></p>"
  },
  "desafio-comentario-mention": {
    event_type: "desafio-comentario-mention",
    display_name: "Menção em Comentário de Desafio",
    description: "Enviado quando um colaborador é mencionado em um comentário de desafio",
    category: "mentions",
    available_variables: [
      { name: "colaborador.nome", description: "Nome do colaborador mencionado", example: "João Silva" },
      { name: "colaborador.email", description: "Email do colaborador", example: "joao@empresa.com" },
      { name: "desafio.id", description: "ID do desafio", example: "chal123" },
      { name: "desafio.titulo", description: "Título do desafio", example: "Desafio Q1" },
      { name: "comentario.id", description: "ID do comentário", example: "cmt789" },
      { name: "comentario.comentario", description: "Texto do comentário", example: "Bom ponto!" },
      { name: "url", description: "URL para acessar o desafio", example: "https://..." }
    ],
    subject_example: "Você foi mencionado | {{desafio.titulo}}",
    body_example:
      "<p>Olá {{colaborador.nome}},</p><p>Você foi mencionado em um comentário no desafio <b>{{desafio.titulo}}</b>:</p><blockquote>{{comentario.comentario}}</blockquote><p><a href='{{url}}'>Ver comentário</a></p>"
  },
  "ideia-movimentacao": {
    event_type: "ideia-movimentacao",
    display_name: "Movimentação de Ideia",
    description: "Enviado quando uma ideia é movimentada no funil",
    category: "movements",
    available_variables: [
      { name: "ideia.id", description: "ID da ideia", example: "idea123" },
      { name: "ideia.titulo", description: "Título da ideia", example: "Nova Proposta" },
      { name: "ideia.status", description: "Novo status da ideia", example: "Em Análise" },
      { name: "movimentacao.observacoes", description: "Observações da movimentação", example: "Aprovado" },
      { name: "movimentacao.data", description: "Data da movimentação", example: "01/01/2025" },
      { name: "colaborador.nome", description: "Nome do autor", example: "Maria Santos" },
      { name: "colaborador.email", description: "Email do autor", example: "maria@empresa.com" },
      { name: "url", description: "URL para acessar a ideia", example: "https://..." }
    ],
    subject_example: "Sua ideia mudou de status: {{ideia.titulo}}",
    body_example:
      "<p>Olá {{colaborador.nome}},</p><p>Sua ideia <b>{{ideia.titulo}}</b> mudou para o status: <b>{{ideia.status}}</b>.</p><p>Observações: {{movimentacao.observacoes}}</p><p><a href='{{url}}'>Acessar ideia</a></p>"
  },
  "colaborador-invite": {
    event_type: "colaborador-invite",
    display_name: "Convite de Colaborador",
    description: "Enviado ao convidar novo colaborador para a empresa",
    category: "invites",
    available_variables: [
      { name: "colaborador.nome", description: "Nome do colaborador convidado", example: "Pedro Costa" },
      { name: "colaborador.email", description: "Email do colaborador", example: "pedro@empresa.com" },
      { name: "licenciada.id", description: "ID da licenciada", example: "lic123" },
      { name: "empresa.id", description: "ID da empresa", example: "emp456" },
      { name: "empresa.nomeFantasia", description: "Nome fantasia da empresa", example: "Empresa XYZ" },
      { name: "inviteUrl", description: "URL do convite", example: "https://..." }
    ],
    subject_example: "Convite para colaborar na {{empresa.nomeFantasia}}",
    body_example:
      "<p>Olá {{colaborador.nome}},</p><p>Você foi convidado para participar da plataforma de inovação da empresa <b>{{empresa.nomeFantasia}}</b>.</p><p>Clique no link abaixo para aceitar o convite:</p><p><a href='{{inviteUrl}}'>Aceitar Convite</a></p>"
  },
  "colaborador-invite-resend": {
    event_type: "colaborador-invite-resend",
    display_name: "Reenvio de Convite",
    description: "Enviado ao reenviar convite para colaborador",
    category: "invites",
    available_variables: [
      { name: "colaborador.nome", description: "Nome do colaborador", example: "Ana Lima" },
      { name: "colaborador.email", description: "Email do colaborador", example: "ana@empresa.com" },
      { name: "licenciada.id", description: "ID da licenciada", example: "lic123" },
      { name: "empresa.id", description: "ID da empresa", example: "emp456" },
      { name: "empresa.nomeFantasia", description: "Nome fantasia da empresa", example: "Empresa ABC" },
      { name: "inviteUrl", description: "URL do convite", example: "https://..." }
    ],
    subject_example: "Lembrete: Convite para colaborar na {{empresa.nomeFantasia}}",
    body_example:
      "<p>Olá {{colaborador.nome}},</p><p>Lembramos que você tem um convite pendente para a empresa <b>{{empresa.nomeFantasia}}</b>.</p><p><a href='{{inviteUrl}}'>Acessar agora</a></p>"
  },
  "colaborador-invite-add": {
    event_type: "colaborador-invite-add",
    display_name: "Adição de Colaborador",
    description: "Enviado ao adicionar colaborador existente à empresa",
    category: "invites",
    available_variables: [
      { name: "colaborador.nome", description: "Nome do colaborador", example: "Carlos Souza" },
      { name: "colaborador.email", description: "Email do colaborador", example: "carlos@empresa.com" },
      { name: "licenciada.id", description: "ID da licenciada", example: "lic123" },
      { name: "empresa.id", description: "ID da empresa", example: "emp789" },
      { name: "empresa.nomeFantasia", description: "Nome fantasia da empresa", example: "Empresa DEF" },
      { name: "inviteUrl", description: "URL de acesso", example: "https://..." }
    ],
    subject_example: "Você foi adicionado à equipe da {{empresa.nomeFantasia}}",
    body_example:
      "<p>Olá {{colaborador.nome}},</p><p>Você agora faz parte da equipe de inovação da <b>{{empresa.nomeFantasia}}</b>.</p><p><a href='{{inviteUrl}}'>Acessar plataforma</a></p>"
  },
  "colaborador-diagnostico-invite": {
    event_type: "colaborador-diagnostico-invite",
    display_name: "Convite para Diagnóstico",
    description: "Enviado ao convidar colaborador para responder diagnóstico",
    category: "invites",
    available_variables: [
      { name: "colaborador.nome", description: "Nome do colaborador", example: "Beatriz Alves" },
      { name: "colaborador.email", description: "Email do colaborador", example: "beatriz@empresa.com" },
      { name: "licenciada.id", description: "ID da licenciada", example: "lic123" },
      { name: "diagnosticoUrl", description: "URL do diagnóstico", example: "https://..." }
    ],
    subject_example: "Convite para responder ao Diagnóstico de Inovação",
    body_example:
      "<p>Olá {{colaborador.nome}},</p><p>Gostaríamos da sua participação em nosso Diagnóstico de Inovação.</p><p><a href='{{diagnosticoUrl}}'>Responder Diagnóstico</a></p>"
  },
  "licenciada-admin-invite": {
    event_type: "licenciada-admin-invite",
    display_name: "Convite de Admin Licenciada",
    description: "Enviado ao convidar administrador para licenciada",
    category: "invites",
    available_variables: [
      { name: "colaborador.nome", description: "Nome do admin convidado", example: "Roberto Dias" },
      { name: "colaborador.email", description: "Email do admin", example: "roberto@licenciada.com" },
      { name: "licenciada.id", description: "ID da licenciada", example: "lic456" },
      { name: "inviteUrl", description: "URL do convite", example: "https://..." }
    ],
    subject_example: "Convite para administrar licenciada",
    body_example:
      "<p>Olá {{colaborador.nome}},</p><p>Você foi convidado para ser administrador.</p><p><a href='{{inviteUrl}}'>Aceitar convite</a></p>"
  },
  "empresa-responsavel-invite": {
    event_type: "empresa-responsavel-invite",
    display_name: "Convite de Responsável de Empresa",
    description: "Enviado ao convidar responsável externo para empresa",
    category: "invites",
    available_variables: [
      { name: "responsavel.nome", description: "Nome do responsável", example: "Sandra Martins" },
      { name: "responsavel.email", description: "Email do responsável", example: "sandra@empresa.com" },
      { name: "empresa.id", description: "ID da empresa", example: "emp123" },
      { name: "empresa.nomeFantasia", description: "Nome fantasia da empresa", example: "Empresa GHI" },
      { name: "inviteUrl", description: "URL do convite", example: "https://..." }
    ],
    subject_example: "Convite para gerenciar a {{empresa.nomeFantasia}}",
    body_example:
      "<p>Olá {{responsavel.nome}},</p><p>Você foi convidado para ser responsável pela empresa <b>{{empresa.nomeFantasia}}</b> na nossa plataforma.</p><p><a href='{{inviteUrl}}'>Aceitar convite</a></p>"
  },
  "projeto-invite-internal": {
    event_type: "projeto-invite-internal",
    display_name: "Convite Interno para Projeto",
    description: "Enviado ao convidar colaborador interno para projeto",
    category: "invites",
    available_variables: [
      { name: "colaborador.nome", description: "Nome do colaborador", example: "Felipe Rocha" },
      { name: "colaborador.email", description: "Email do colaborador", example: "felipe@empresa.com" },
      { name: "projeto.nome", description: "Nome do projeto", example: "Projeto Alpha" },
      { name: "empresa.nomeFantasia", description: "Nome fantasia da empresa", example: "Empresa JKL" },
      { name: "inviteUrl", description: "URL do convite", example: "https://..." }
    ],
    subject_example: "Convite para o projeto {{projeto.nome}}",
    body_example:
      "<p>Olá {{colaborador.nome}},</p><p>Você foi convidado para participar do projeto <b>{{projeto.nome}}</b>.</p><p><a href='{{inviteUrl}}'>Ver projeto</a></p>"
  },
  "projeto-invite-external": {
    event_type: "projeto-invite-external",
    display_name: "Convite Externo para Projeto",
    description: "Enviado ao convidar responsável externo para projeto",
    category: "invites",
    available_variables: [
      { name: "responsavel.nome", description: "Nome do responsável", example: "Luciana Freitas" },
      { name: "responsavel.email", description: "Email do responsável", example: "luciana@externa.com" },
      { name: "projeto.nome", description: "Nome do projeto", example: "Projeto Beta" },
      { name: "inviteUrl", description: "URL do convite", example: "https://..." }
    ],
    subject_example: "Convite externo para o projeto {{projeto.nome}}",
    body_example:
      "<p>Olá {{responsavel.nome}},</p><p>Você foi convidado como participante externo do projeto <b>{{projeto.nome}}</b>.</p><p><a href='{{inviteUrl}}'>Acessar projeto</a></p>"
  },
  "user-password-reset": {
    event_type: "user-password-reset",
    display_name: "Reset de Senha",
    description: "Enviado ao usuário solicitar reset de senha",
    category: "auth",
    available_variables: [
      { name: "user.nome", description: "Nome do usuário", example: "Eduardo Gomes" },
      { name: "user.email", description: "Email do usuário", example: "eduardo@empresa.com" },
      { name: "resetLink", description: "Link para reset de senha", example: "https://..." }
    ],
    subject_example: "Redefinição de senha",
    body_example:
      "<p>Olá {{user.nome}},</p><p>Recebemos uma solicitação para redefinir sua senha.</p><p><a href='{{resetLink}}'>Redefinir Senha</a></p>"
  },
  "premium-report-pdf": {
    event_type: "premium-report-pdf",
    display_name: "Relatório Premium em PDF",
    description: "Enviado ao gerar relatório premium com PDF anexo",
    category: "reports",
    available_variables: [
      { name: "report.nome", description: "Nome do relatório", example: "Relatório Q1" },
      { name: "report.empresaNome", description: "Nome da empresa", example: "Empresa MNO" },
      { name: "report.filename", description: "Nome do arquivo PDF", example: "relatorio-q1.pdf" },
      { name: "licenciada.id", description: "ID da licenciada", example: "lic789" },
      { name: "pdfUrl", description: "URL do PDF (se disponível)", example: "https://..." }
    ],
    subject_example: "Seu relatório está pronto: {{report.filename}}",
    body_example:
      "<p>Olá,</p><p>O relatório <b>{{report.nome}}</b> da empresa {{report.empresaNome}} foi gerado com sucesso.</p><p><a href='{{pdfUrl}}'>Baixar PDF</a></p>"
  }
};

/**
 * Get event type display name
 * @param eventType
 */
export function getEventDisplayName(eventType: EmailEventType): string {
  return EMAIL_EVENT_METADATA[eventType]?.display_name || eventType;
}

/**
 * Get event type category
 * @param eventType
 */
export function getEventCategory(eventType: EmailEventType): string {
  return EMAIL_EVENT_METADATA[eventType]?.category || "general";
}

/**
 * Get all event types by category
 */
export function getEventsByCategory(): Record<string, EmailEventType[]> {
  const byCategory: Record<string, EmailEventType[]> = {};

  for (const [eventType, metadata] of Object.entries(EMAIL_EVENT_METADATA)) {
    const category = metadata.category;
    if (!byCategory[category]) {
      byCategory[category] = [];
    }
    byCategory[category].push(eventType as EmailEventType);
  }

  return byCategory;
}

/**
 * Category display names
 */
export const CATEGORY_NAMES: Record<string, string> = {
  mentions: "Menções",
  invites: "Convites",
  movements: "Movimentações",
  reports: "Relatórios",
  auth: "Autenticação"
};
